/**
 * @file Public landing page — pages/Landing/GlossaryPage.tsx.
 */

import React from "react";
import { Link } from "react-router-dom";
import "./LandingPage.css";
import "./ContentPage.css";

const GlossaryPage: React.FC = () => {
  return (
    <div className="landing-root">
      <header className="landing-header flex justify-center items-center space-x-3 text-center py-4">
        <h1 className="landing-main-title text-4xl font-bold text-primary">
          ICoMEC
        </h1>
        <h2 className="landing-sub-title text-xl text-grayDark">
          Indian Construction Materials Embodied Carbon Data Base
        </h2>
      </header>

      <nav className="landing-nav">
        <Link to="/">Home</Link>
        <Link to="/join">Join</Link>
        <Link to="/impact">Impact</Link>
        <Link to="/research">Research and Analysis</Link>
        <Link to="/about">About</Link>
      </nav>

      <section className="content-page">
        <h1 className="content-title">Glossary</h1>
        
        <div className="glossary-section">
          <h3>Life Cycle Assessment (LCA) Stages</h3>
          <p>The platform focuses on the Cradle-to-Gate stages of construction materials, categorized as follows:</p>
          
          <div className="glossary-term">
            <strong>A1: Raw Material Supply</strong>
            <p>Includes the extraction and processing of raw materials (mining, biomass production), reuse of secondary materials, and the generation/transport of primary energy (electricity, steam, heat) required for these processes.</p>
          </div>

          <div className="glossary-term">
            <strong>A2: Transport</strong>
            <p>Includes the transportation of raw materials from the extraction site to the factory gate, as well as any internal transport within the supply chain.</p>
          </div>

          <div className="glossary-term">
            <strong>A3: Manufacturing</strong>
            <p>Includes the production of ancillary materials, manufacturing of the finished construction products and co-products, and the manufacturing of required packaging.</p>
          </div>

          <div className="glossary-term">
            <strong>A1–A3 (Cradle-to-Gate)</strong>
            <p>The combined impact of the A1, A2, and A3 stages, representing the total environmental footprint from resource extraction to the point where the product leaves the manufacturing facility.</p>
          </div>
        </div>

        <div className="glossary-section">
          <h3>Material and Production Definitions</h3>
          
          <div className="glossary-term">
            <strong>Finished Construction Material</strong>
            <p>The final product analyzed (e.g., Cement, Steel, Bricks).</p>
          </div>

          <div className="glossary-term">
            <strong>Material Sub-type</strong>
            <p>Sub-classifications based on specific properties or raw materials (e.g., Ordinary Portland Cement (OPC) vs. Pozzolana Portland Cement (PPC)).</p>
          </div>

          <div className="glossary-term">
            <strong>Raw Material</strong>
            <p>The primary inputs required for production (e.g., limestone and clay for cement manufacturing).</p>
          </div>

          <div className="glossary-term">
            <strong>Declared Quantity</strong>
            <p>The specific amount of finished material for which data is collected (e.g., 1 Metric Tonne).</p>
          </div>

          <div className="glossary-term">
            <strong>Functional Unit</strong>
            <p>A quantified performance requirement of a product system used as a reference unit for environmental impact comparisons.</p>
          </div>
        </div>

        <div className="glossary-section">
          <h3>Environmental Impact Metrics</h3>
          
          <div className="glossary-term">
            <strong>Embodied Energy (A1, A2, A3, or A1–A3)</strong>
            <p>The total primary energy consumed during the specified life cycle stage(s), measured in MJ or kWh per declared quantity.</p>
          </div>

          <div className="glossary-term">
            <strong>Embodied Carbon (A1, A2, A3, or A1–A3)</strong>
            <p>The total greenhouse gas emissions (CO₂e) released during the specified life cycle stage(s).</p>
          </div>

          <div className="glossary-term">
            <strong>Calorific Value</strong>
            <p>The energy content of a fuel, determined by the heat produced during complete combustion.</p>
          </div>

          <div className="glossary-term">
            <strong>Fuel Mix</strong>
            <p>The specific types and proportions of fuels utilized across extraction, transport, and manufacturing processes.</p>
          </div>
        </div>

        <div className="glossary-section">
          <h3>Logistics and Technical Parameters</h3>
          
          <div className="glossary-term">
            <strong>Extraction Site / Manufacturing Plant</strong>
            <p>The specific geographic locations (defined by Latitude and Longitude) where materials are sourced and produced.</p>
          </div>

          <div className="glossary-term">
            <strong>Vehicle Specifications</strong>
            <p>Data regarding the Category (Light, Medium, or Heavy-Duty), Make, Model, and Capacity (tonnes) of vehicles used for A2 transport.</p>
          </div>

          <div className="glossary-term">
            <strong>Extraction Processes</strong>
            <p>The technical methods involved in mining or procuring raw material components.</p>
          </div>
        </div>

        <div className="glossary-section">
          <h3>Data Provenance and Documentation</h3>
          <p>ICoMEC prioritizes data traceability through the following sources:</p>
          
          <div className="glossary-term">
            <strong>Environmental Product Declaration (EPD)</strong>
            <p>A document providing quantified environmental data using predetermined indicators and, where relevant, additional environmental information.</p>
          </div>

          <div className="glossary-term">
            <strong>Peer-Reviewed Literature</strong>
            <p>Data sourced from verified Journal or Conference publications.</p>
          </div>

          <div className="glossary-term">
            <strong>Grey Literature</strong>
            <p>Technically reviewed but unofficially published reports, such as industry white papers, ESG reports, or government scheme documentation.</p>
          </div>

          <div className="glossary-term">
            <strong>LCA Report / Sustainability Report</strong>
            <p>Comprehensive internal or external reports detailing a manufacturer's environmental performance.</p>
          </div>
        </div>
      </section>

      <footer className="landing-footer">
        <div className="footer-main">
          <div className="footer-left">
            <h2>ICoMEC</h2>
            <p>
              Indian Construction Materials Embodied
              <br /> Carbon Data Base
            </p>
          </div>

          <div className="footer-links">
            <div>
              <h4>About Us</h4>
              <ul>
                <li><Link to="/who-we-are">Who We Are</Link></li>
                <li><Link to="/what-we-do">What We Do</Link></li>
                <li><Link to="/leadership">Leadership</Link></li>
                <li><Link to="/partners">Partners</Link></li>
                <li><Link to="/faq">FAQ</Link></li>
                <li><Link to="/glossary">Glossary</Link></li>
              </ul>
            </div>
            <div>
              <h4>Resources</h4>
              <ul>
                {/* <li><Link to="/resource-library">Resource Library</Link></li> */}
                <li><Link to="/events">Events</Link></li>
              </ul>
            </div>
          </div>
        </div>
      </footer>

      <div className="footer-bottom">
        <p>
          <Link to="/cookies-policy">Cookies Policy</Link> · <Link to="/privacy-policy">Privacy Policy</Link> · <Link to="/legal">Legal</Link> · <Link to="/terms-of-use">Terms of Use</Link>
          {/* · <Link to="/imprint">Imprint</Link> */}
        </p>
        <p>© 2026 CARBSE. All rights reserved.</p>
      </div>
    </div>
  );
};

/** React component — Glossary Page. */
export default GlossaryPage;
